import { useCallback, useMemo } from "react";
import { useStopAgentExecution, useCreateAgentExecutionConversationToken } from "@/queries/agent-queries";
import { toast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import {
    downloadAndSaveSupportBundle,
    downloadAgentExecutionSupportBundle,
} from "@/utils/support-bundle";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import { useEnvironment, useCreateEnvironmentLogsToken } from "@/queries/environment-queries";
import { loadFromCache } from "@/utils/stream-cache";
import { getConversationCacheKey } from "@/utils/dashboard-conversation-stream";
import type JSZip from "jszip";

async function downloadCachedConversationData(zip: JSZip, agentExecutionId: string): Promise<void> {
    try {
        const cacheKey = getConversationCacheKey(agentExecutionId);
        const cachedData = await loadFromCache(cacheKey);

        if (cachedData && cachedData.chunk.length > 0) {
            zip.file("conversation-stream.jsonl", cachedData.chunk);
        } else {
            const message = `No cached conversation data available for agent execution ${agentExecutionId}\nThis contains the conversation data as last seen by the user.\nTimestamp: ${new Date().toISOString()}`;
            zip.file("conversation-stream-unavailable.txt", message);
        }
    } catch (error) {
        const errorMessage = `Failed to load cached conversation data: ${String(error)}\nTimestamp: ${new Date().toISOString()}`;
        zip.file("conversation-stream-error.txt", errorMessage);
    }
}

export const useAgentExecutionActions = (agentExecution?: PlainAgentExecution) => {
    const stopAgentExecution = useStopAgentExecution();

    const lastEnvironmentId = useMemo(() => {
        return agentExecution?.status?.usedEnvironments?.[agentExecution.status.usedEnvironments.length - 1]
            ?.environmentId;
    }, [agentExecution?.status?.usedEnvironments]);

    const { data: environment } = useEnvironment(lastEnvironmentId);
    const { data: logsToken } = useCreateEnvironmentLogsToken(lastEnvironmentId);
    const { data: agentExecutionToken } = useCreateAgentExecutionConversationToken(agentExecution?.id);

    const copyAgentExecutionId = useCallback(async () => {
        if (!agentExecution?.id) {
            toast({ title: "No session ID available to copy" });
            return;
        }
        await navigator.clipboard.writeText(agentExecution.id);
        toast({ title: "Session ID copied to clipboard" });
    }, [agentExecution?.id]);

    const handleStopAgentExecution = useCallback(async () => {
        if (!agentExecution?.id) {
            toast({ title: "No session ID available to stop" });
            return;
        }
        try {
            await stopAgentExecution.mutateAsync({ agentExecutionId: agentExecution.id });
        } catch (error) {
            toast({
                title: "Failed to stop session",
                description: formatError(error),
            });
        }
    }, [agentExecution?.id, stopAgentExecution]);

    const agentExecutionSupportBundleUrl = agentExecution?.status?.supportBundleUrl;
    const environmentSupportBundleUrl = environment?.status?.environmentUrls?.supportBundle;

    const downloadSupportBundle = useCallback(async () => {
        if (!agentExecution?.id) {
            return;
        }

        // Need at least one bundle URL and its corresponding token
        const hasEnvBundle = !!environmentSupportBundleUrl && !!logsToken;
        const hasAgentBundle = !!agentExecutionSupportBundleUrl && !!agentExecutionToken;
        if (!hasEnvBundle && !hasAgentBundle) {
            return;
        }

        const generatingToast = toast({ title: "Generating support bundle...", indefinite: true });
        try {
            await downloadAndSaveSupportBundle({
                supportBundleUrl: environmentSupportBundleUrl ?? "",
                logsToken: logsToken ?? "",
                filename: `support-bundle-${agentExecution.id}.zip`,
                skipEnvironmentBundle: !hasEnvBundle && !hasAgentBundle,
                addExtras: async (zip) => {
                    await downloadCachedConversationData(zip, agentExecution.id);
                    if (agentExecutionSupportBundleUrl && agentExecutionToken) {
                        await downloadAgentExecutionSupportBundle(
                            zip,
                            agentExecutionSupportBundleUrl,
                            agentExecutionToken,
                        );
                    }
                },
            });
            generatingToast.dismiss();
            toast({ title: "Support bundle downloaded" });
        } catch (error) {
            generatingToast.dismiss();
            toast({
                title: "Failed to download support bundle",
                description: formatError(error),
            });
        }
    }, [
        environmentSupportBundleUrl,
        agentExecutionSupportBundleUrl,
        logsToken,
        agentExecutionToken,
        agentExecution?.id,
    ]);

    const isSupportBundleAvailable =
        (!!environmentSupportBundleUrl && !!logsToken) ||
        (!!agentExecutionSupportBundleUrl && !!agentExecutionToken);

    return {
        copyAgentExecutionId,
        handleStopAgentExecution,
        downloadSupportBundle,
        isStopPending: stopAgentExecution.isPending,
        isSupportBundleAvailable,
    };
};
